import 'react-loading-skeleton/dist/skeleton.css';

import {
  ACCOUNT_ID,
  usePublicGroupDetailsById,
  usePublicPortfolioByAccount,
} from '@utils/apiHooks';
import { useMobileWidth, useScrollDirection } from '@utils/hooks';
import { useCallback, useMemo, useState } from 'react';
import {
  BodyWrapper,
  DescriptionText,
  FooterTitle,
  LogoWrapper,
  PartnerItemsContainer,
  PartnerModalRoot,
  PartnersDescriptionText,
  PartnersListContainer,
  PartnersSectionRoot,
  PartnersTitle,
  PartnersTitleWrapper,
  Tag,
  TagsWrapper,
} from './styles';

import { Column } from '@components/LinearLayout';
import Modal from '@components/Modal';
import ScrollToTop from '@components/ScrollToTop';
import { PSmall } from '@components/Typography';
import { useInView } from '@react-spring/web';
import { SCROLL_DOWN_MODE } from '@utils/constants';
import { getImageSrc } from '@utils/fn';
import { sanitizeHtmlWithoutTargetAttrInAnchor } from '@utils/sanitize';
import { useTranslation } from 'react-i18next';
import { useInView as useInViewObserver } from 'react-intersection-observer';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import PartnerItem from '../PartnerItem';

interface Partner {
  id: number;
  link: string;
  description: string;
  eventName: string;
  themeColor: string;
}

interface PartnersSectionProps {
  groupId?: string;
}
const PartnersSection = ({ groupId }: PartnersSectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { portfolioSlug } = useParams();
  const scrollDirection = useScrollDirection();
  const isMobile = useMobileWidth();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedPartner, setSelectedPartner] = useState<null | Partner>();
  const [ref, inView] = useInViewObserver({ threshold: 0.4 });

  const { data: portfolios, isSuccess } = usePublicPortfolioByAccount({
    accountId: ACCOUNT_ID,
  });

  const exhibitorGroupId = useMemo(
    () =>
      groupId ??
      portfolios?.data?.filter(e => e?.slug === portfolioSlug)?.[0]?.details
        ?.exhibitorGroup,
    [groupId, portfolioSlug, portfolios?.data]
  );

  const { data: exhibitorGroupData, isLoading: isExhibitorGroupLoading } =
    usePublicGroupDetailsById({
      groupId: exhibitorGroupId as string,
      enabled: (!!groupId || !!exhibitorGroupId) && isSuccess,
    });

  const partnersItems = useMemo(
    () =>
      exhibitorGroupData?.list
        ?.sort((a, b) => {
          const orderA = a?.order ?? Number.MAX_SAFE_INTEGER;
          const orderB = b?.order ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        })
        ?.map(exhibitor => ({
          id: exhibitor?.id,
          link: getImageSrc(exhibitor?.company?.logo),
          description: exhibitor?.company?.about,
          eventName: exhibitor?.eventName,
          themeColor: exhibitor?.eventDesign?.themeColor,
        })),
    [exhibitorGroupData?.list]
  );

  const [topRef, topSprings] = useInView(() =>
    scrollDirection === 'down'
      ? {
          from: {
            opacity: 0,
            y: 100,
          },
          to: {
            opacity: 1,
            y: 0,
          },

          delay: 3500,
          immediate: false,
          threshold: 1,
        }
      : {
          from: {
            opacity: 0,
            y: -100,
          },
          to: {
            opacity: 1,
            y: 0,
          },
          delay: 3500,
          immediate: false,
          threshold: 1,
        }
  );

  const [bottomRef, bottomSprings] = useInView(() =>
    scrollDirection === 'down'
      ? {
          from: {
            opacity: 0,
            y: 100,
          },
          to: {
            opacity: 1,
            y: 0,
          },
          delay: 3500,
          immediate: false,
          threshold: 1,
        }
      : {
          from: {
            opacity: 0,
            y: -100,
          },
          to: {
            opacity: 1,
            y: 0,
          },
          delay: 3500,
          immediate: false,
          threshold: 1,
        }
  );

  const PartnerModal = useMemo(() => {
    if (selectedPartner == null) return;
    return (
      <PartnerModalRoot>
        <LogoWrapper>
          {selectedPartner?.link?.length > 0 && (
            <img
              src={selectedPartner?.link}
              width={200}
              height={100}
              alt={selectedPartner?.eventName}
              loading="lazy"
            />
          )}
        </LogoWrapper>
        <BodyWrapper>
          <DescriptionText
            dangerouslySetInnerHTML={{
              __html: sanitizeHtmlWithoutTargetAttrInAnchor(
                selectedPartner?.description
              ),
            }}
          />

          <Column $gap="1rem">
            <FooterTitle> {t('business-partner-at')}:</FooterTitle>
            <TagsWrapper>
              <Tag color={selectedPartner?.themeColor}>
                <PSmall>{selectedPartner?.eventName}</PSmall>
              </Tag>
            </TagsWrapper>
          </Column>
        </BodyWrapper>
      </PartnerModalRoot>
    );
  }, [selectedPartner, t]);

  const PartnerSkeleton = useCallback(() => {
    return (
      <Skeleton
        count={1}
        height="12.6rem"
        width={isMobile ? '85vw' : '27.3rem'}
        borderRadius={theme.borderRadius.radius10}
        enableAnimation
      />
    );
  }, [isMobile, theme.borderRadius.radius10]);

  return (
    <PartnersSectionRoot ref={ref}>
      <PartnersListContainer>
        <PartnersTitleWrapper ref={topRef} style={topSprings}>
          <PartnersTitle> {t('partners-section-title')}</PartnersTitle>
          <PartnersDescriptionText>{t('registered-attendees-text')}</PartnersDescriptionText>
        </PartnersTitleWrapper>
        {isExhibitorGroupLoading ? (
          <PartnerItemsContainer ref={bottomRef} style={bottomSprings}>
            {Array.from({ length: 5 }, (_, index: number) => {
              return <PartnerSkeleton key={`${index}-blog`} />;
            })}
          </PartnerItemsContainer>
        ) : (
          <PartnerItemsContainer ref={bottomRef} style={bottomSprings}>
            {partnersItems?.map(item => (
              <PartnerItem
                borderRadius="0.4rem"
                key={item.id}
                link={item.link}
                onClick={() => {
                  setShowModal(true);
                  setSelectedPartner(item);
                }}
              />
            ))}
          </PartnerItemsContainer>
        )}
      </PartnersListContainer>
      <Modal
        visible={showModal}
        maxHeight="90vh"
        onClose={() => {
          setShowModal(false);
          setSelectedPartner(null);
        }}
        content={PartnerModal}
      />

      {inView && <ScrollToTop mode={SCROLL_DOWN_MODE.LIGHT} />}
    </PartnersSectionRoot>
  );
};

export default PartnersSection;
